"use client"

import React, { useState } from "react"
import { useLanguage } from "../../contexts/LanguageContext"
import { Flex, Grid, Card, Badge, Text, Heading, Box, Button, Avatar, ScrollArea } from "@radix-ui/themes"
import { MapPin, Clock, Truck, User, ChevronRight } from "lucide-react"

interface Order {
    _id: string
    orderNumber?: string
    status: string
    orderSource: string
    customer?: { _id: string; username: string; phoneNumber: string; fullName?: string }
    customerPhone?: string
    customerName?: string
    restaurant: { _id: string; name: string; logo?: string }
    captain?: { _id: string; name: string; phone: string }
    items: Array<{ foodItem: { name: string }; quantity: number; price: number; size?: string }>
    pricing: { subtotal: number; deliveryFee: number; total: number }
    deliveryAddress: { street?: string; city?: string; fullAddress?: string } | string
    specialInstructions?: string
    createdAt: string
}

interface OrdersKanbanProps {
    orders: Order[]
    onStatusUpdate: (orderId: string, newStatus: string) => void
    onOrderClick: (order: Order) => void
}

const OrdersKanban: React.FC<OrdersKanbanProps> = ({ orders, onStatusUpdate, onOrderClick }) => {
    const { t, isRTL } = useLanguage()

    const columns = [
        { id: "pending", title: t("pending"), color: "yellow" },
        { id: "confirmed", title: t("confirmed"), color: "blue" },
        { id: "preparing", title: t("preparing"), color: "orange" },
        { id: "ready", title: t("ready"), color: "purple" },
        { id: "on-the-way", title: t("onTheWay"), color: "indigo" },
        { id: "delivered", title: t("delivered"), color: "green" },
    ]

    const getOrdersByStatus = (status: string) => {
        return orders.filter((order) => order.status === status)
    }

    const formatTime = (dateString: string) => {
        return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }

    return (
        <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
                {columns.map((column) => {
                    const columnOrders = getOrdersByStatus(column.id)
                    return (
                        <div key={column.id} className="w-72 flex-shrink-0 flex flex-col gap-3">
                            <div className={`p-3 rounded-lg bg-${column.color}-100 dark:bg-${column.color}-900/30 border border-${column.color}-200 dark:border-${column.color}-800`}>
                                <Flex justify="between" align="center">
                                    <Text weight="bold" size="2" color={column.color as any}>
                                        {column.title}
                                    </Text>
                                    <Badge color={column.color as any} variant="solid" radius="full">
                                        {columnOrders.length}
                                    </Badge>
                                </Flex>
                            </div>

                            <ScrollArea type="auto" scrollbars="vertical" style={{ maxHeight: "calc(100vh - 250px)" }}>
                                <Flex direction="column" gap="3" className="pr-2">
                                    {columnOrders.length === 0 ? (
                                        <div className="p-4 text-center border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                                            <Text size="1" color="gray">{t("noOrders")}</Text>
                                        </div>
                                    ) : (
                                        columnOrders.map((order) => (
                                            <Card
                                                key={order._id}
                                                className="hover:shadow-md transition-shadow cursor-pointer border-l-4"
                                                style={{ borderLeftColor: `var(--${column.color}-9)` }}
                                                onClick={() => onOrderClick(order)}
                                            >
                                                <Flex direction="column" gap="2">
                                                    <Flex justify="between" align="start">
                                                        <Box>
                                                            <Text weight="bold" size="2">#{order.orderNumber || order._id.slice(-6)}</Text>
                                                            <Text size="1" color="gray" className="block">
                                                                {formatTime(order.createdAt)}
                                                            </Text>
                                                        </Box>
                                                        <Badge size="1" variant="soft" color="gray">SDG {order.pricing?.total?.toFixed(2)}
                                                        </Badge>
                                                    </Flex>

                                                    <Flex align="center" gap="2">
                                                        <User size={14} className="text-gray-400" />
                                                        <Text size="1">{order.customer?.fullName || order.customer?.username || t("guest")}</Text>
                                                    </Flex>

                                                    <Flex align="center" gap="2">
                                                        <MapPin size={14} className="text-gray-400" />
                                                        <Text size="1" className="truncate w-40">{order.restaurant?.name}</Text>
                                                    </Flex>

                                                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex justify-end">
                                                        <Button size="1" variant="ghost" onClick={(e) => {
                                                            e.stopPropagation()
                                                            onOrderClick(order)
                                                        }}>
                                                            {t("details")} <ChevronRight size={14} />
                                                        </Button>
                                                    </div>
                                                </Flex>
                                            </Card>
                                        ))
                                    )}
                                </Flex>
                            </ScrollArea>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default OrdersKanban
